/* Quick add and quick view (docs/17; builds on docs/15 §9 overlay and §10 product card).

   A card dispatches `mw:quick-add` / `mw:quick-view` (assets/card.js, detail
   { handle, url, variantId, trigger }). This module answers by opening the matching dialog from
   the Overlay group and filling it with the product's own Liquid, fetched through Shopify's
   Section Rendering API:

     <product url>?section_id=<quick-add|quick-view section>[&variant=…][&option_values=…]

   The response carries a <template data-quick-panel>; its content goes into the dialog body.
   Nothing here builds markup from JavaScript: price, badges, option availability, inventory and
   media are all rendered by the server, so a 250-variant product costs the client nothing beyond
   one request per option change. Responses are cached per request URL for the page's lifetime.

   If the request fails — offline, a deleted section, a 404 — the shopper still gets the product:
   the dialog closes and the browser navigates to the product page, which is also what happens
   when no quick dialog is installed at all (card.js's own fallback).

   Custom elements defined here, all usable outside the dialogs (the product page will reuse them):
     <mw-variant-picker>  snippets/variant-picker.liquid — re-renders [data-variant-root] on change
     <mw-quantity>        snippets/product-form.liquid   — quantity stepper with the variant's rules
     <mw-quick-gallery>   sections/quick-view.liquid      — scroll-snap gallery with thumbnails
     <mw-more>            sections/quick-view.liquid      — clamped description with Show more

   API: window.Munkwell.quick.open('add' | 'view', { url, variantId, trigger }) → Promise<boolean> */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const ADDED_MS = 2200;

const panels = new Map(); // request url → panel markup
const shells = new WeakMap(); // dialog body → its loading markup as rendered by Liquid
const requests = new WeakMap(); // dialog → id of the request it is waiting for
let nextRequest = 0;

/* ---------- Fetching ---------- */

/* Builds the Section Rendering API URL for a product, keeping the product's own path. */
function sectionUrl(productUrl, sectionId, params = {}) {
  const url = new URL(productUrl, window.location.href);
  url.searchParams.set('section_id', sectionId);
  Object.entries(params).forEach(([key, value]) => {
    if (value) url.searchParams.set(key, String(value));
    else url.searchParams.delete(key);
  });
  return `${url.pathname}${url.search}`;
}

/* Quick add and quick view wrap their panel in a <template>; the product page's section is the
   panel itself (its grid carries data-variant-root). Both come back as markup holding a
   [data-variant-root]. Without the second case the product page's picker could not re-render
   and the form kept the first variant (found 21.09). */
function panelFrom(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const template = doc.querySelector('template[data-quick-panel]');
  if (template) return template.innerHTML;
  const root = doc.querySelector('[data-variant-root]');
  if (root) return root.outerHTML;
  throw new Error('quick: the section returned no panel');
}

async function loadPanel(request) {
  const cached = panels.get(request);
  if (cached) return cached;
  const response = await fetch(request, { headers: { Accept: 'text/html' } });
  if (!response.ok) throw new Error(response.statusText || 'quick: request failed');
  const markup = panelFrom(await response.text());
  panels.set(request, markup);
  return markup;
}

/* ---------- Dialog body states ---------- */

function shellOf(body) {
  if (!shells.has(body)) shells.set(body, body.innerHTML);
  return shells.get(body);
}

function showLoading(body) {
  body.innerHTML = shellOf(body);
  body.setAttribute('aria-busy', 'true');
}

function showPanel(body, markup) {
  body.innerHTML = markup;
  body.removeAttribute('aria-busy');
  body.scrollTop = 0;
}

/* ---------- Opening ---------- */

const dialogFor = (kind) => document.querySelector(`dialog.overlay[data-quick="${kind}"]`);

async function open(kind, detail = {}) {
  const dialog = dialogFor(kind);
  const overlay = window.Munkwell?.overlay;
  const body = dialog?.querySelector('[data-quick-body]');
  const sectionId = dialog?.dataset.sectionId;
  if (!dialog || !overlay || !body || !sectionId || !detail.url) return false;

  const request = sectionUrl(detail.url, sectionId, { variant: detail.variantId });
  const cached = panels.get(request);
  if (cached) showPanel(body, cached);
  else showLoading(body);
  if (!overlay.open(dialog, detail.trigger || null)) return false;
  if (cached) return true;

  const token = (nextRequest += 1);
  requests.set(dialog, token);
  try {
    const markup = await loadPanel(request);
    if (requests.get(dialog) !== token) return true;
    showPanel(body, markup);
  } catch {
    if (requests.get(dialog) !== token) return true;
    // The shopper asked for this product: give them the product page rather than an apology.
    overlay.close(dialog);
    window.location.assign(detail.url);
  }
  return true;
}

function handOff(event, kind) {
  if (event.defaultPrevented) return;
  const detail = event.detail || {};
  if (!dialogFor(kind) || !detail.url) return; // card.js navigates instead
  event.preventDefault();
  open(kind, detail);
}

document.addEventListener('mw:quick-add', (event) => handOff(event, 'add'));
document.addEventListener('mw:quick-view', (event) => handOff(event, 'view'));

/* ---------- Variant picker ---------- */

/* ---------- Colour change: the band changes colour in place ----------
   The model photos are framed so the band sits on the same pixels in every colour
   (sections/main-product.liquid). So the new colour's first photo is decoded before the swap, and
   the old photo is laid over it and faded off: the face holds still and only the band changes.
   Nothing happens when the first photo stays the same, is off screen, or motion is reduced. */
const firstSlideImg = (scope) => scope.querySelector('.pdp-gallery__track > .pdp-gallery__item:first-child img');

async function holdFirstSlide(root, fresh) {
  const now = firstSlideImg(root);
  const next = firstSlideImg(fresh);
  if (!now || !next) return null;
  const path = (src) => (src || '').split('?')[0];
  if (path(now.currentSrc || now.src) === path(next.getAttribute('src'))) return null;
  // Warm the exact candidate the new image will pick (same srcset and sizes), at most 700 ms.
  const probe = new Image();
  probe.sizes = next.getAttribute('sizes') || '';
  probe.srcset = next.getAttribute('srcset') || '';
  probe.src = next.getAttribute('src');
  await Promise.race([probe.decode().catch(() => {}), new Promise((resolve) => setTimeout(resolve, 700))]);
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return null;
  const box = now.getBoundingClientRect();
  if (!box.width || box.bottom < 0 || box.top > innerHeight || box.right < 0 || box.left > innerWidth) return null;
  const ghost = now.cloneNode();
  ghost.removeAttribute('id');
  ghost.removeAttribute('loading');
  ghost.alt = '';
  ghost.className = 'pdp-gallery__ghost';
  return ghost;
}

function releaseFirstSlide(root, ghost) {
  const item = ghost && firstSlideImg(root)?.closest('.pdp-gallery__item');
  if (!item) return;
  item.append(ghost);
  const done = () => ghost.remove();
  ghost.addEventListener('transitionend', done, { once: true });
  setTimeout(done, 900);
  requestAnimationFrame(() => requestAnimationFrame(() => ghost.classList.add('is-leaving')));
}

class MwVariantPicker extends HTMLElement {
  connectedCallback() {
    if (this.wired) return;
    this.wired = true;
    this.addEventListener('change', (event) => this.onChange(event));
  }

  /* The chosen option value id per option, in option order — Shopify's `option_values` order. */
  valueIds() {
    const chosen = new Map();
    this.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
      chosen.set(Number(input.dataset.optionPosition), input.dataset.valueId);
    });
    this.querySelectorAll('select[data-option-position]').forEach((select) => {
      const option = select.selectedOptions[0];
      if (option) chosen.set(Number(select.dataset.optionPosition), option.dataset.valueId);
    });
    return [...chosen.entries()].sort((a, b) => a[0] - b[0]).map(([, id]) => id);
  }

  async onChange(event) {
    const control = event.target;
    if (!(control instanceof HTMLElement) || !control.matches('input[type="radio"], select')) return;
    const chosen = control.matches('select') ? control.selectedOptions[0] : control;
    if (!chosen) return;

    // Immediate feedback on the label beside the option name, before the server answers.
    const label = control.closest('.vpick__option')?.querySelector('[data-vpick-value]');
    if (label) label.textContent = chosen.value || chosen.textContent.trim();

    const root = this.closest('[data-variant-root]');
    const productUrl = this.dataset.productUrl;
    const sectionId = this.dataset.sectionId;
    if (!root || !sectionId || !productUrl) {
      // No section to re-render against (or no JS runtime for it): the option value's own URL is
      // a complete, server-rendered answer.
      if (chosen.dataset.url) window.location.assign(chosen.dataset.url);
      return;
    }

    const request = sectionUrl(productUrl, sectionId, { option_values: this.valueIds().join(',') });
    const token = (nextRequest += 1);
    this.token = token;
    root.setAttribute('aria-busy', 'true');
    root.classList.add('is-loading');

    let fresh = null;
    try {
      const markup = await loadPanel(request);
      const holder = document.createElement('template');
      holder.innerHTML = markup;
      fresh = holder.content.querySelector('[data-variant-root]');
    } catch {
      fresh = null;
    }
    if (this.token !== token || !root.isConnected) return;

    if (!fresh) {
      // Keep the picker usable: point the form at the variant this value belongs to.
      root.removeAttribute('aria-busy');
      root.classList.remove('is-loading');
      const input = root.querySelector('[data-variant-input]');
      if (input && chosen.dataset.variantId) input.value = chosen.dataset.variantId;
      return;
    }

    // A new colour's first photo: decoded before the swap, then the old one fades off it.
    const ghost = await holdFirstSlide(root, fresh);
    if (this.token !== token || !root.isConnected) return;

    const focusId = chosen.dataset.inputId;
    root.innerHTML = fresh.innerHTML;
    releaseFirstSlide(root, ghost);
    root.removeAttribute('aria-busy');
    root.classList.remove('is-loading');

    const next = focusId && root.querySelector(`[data-input-id="${CSS.escape(focusId)}"]`);
    if (next) (next instanceof HTMLOptionElement ? next.closest('select') : next)?.focus({ preventScroll: true });

    if (this.hasAttribute('data-history')) {
      const id = root.querySelector('[data-variant-input]')?.value;
      if (id) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', id);
        window.history.replaceState({}, '', url);
      }
    }

    root.dispatchEvent(new CustomEvent('mw:variant:change', { bubbles: true, detail: { root } }));
  }
}

/* ---------- Quantity ---------- */

class MwQuantity extends HTMLElement {
  connectedCallback() {
    if (this.wired) return;
    this.wired = true;
    this.input = this.querySelector('input[type="number"]');
    if (!this.input) return;
    this.addEventListener('click', (event) => {
      const step = event.target.closest?.('[data-qty-step]');
      if (!step) return;
      this.nudge(Number(step.dataset.qtyStep) || 0);
    });
    this.input.addEventListener('change', () => this.clamp());
    this.sync();
  }

  bounds() {
    const min = Number(this.input.min) || 1;
    const max = this.input.max ? Number(this.input.max) : Infinity;
    const step = Number(this.input.step) || 1;
    return { min, max, step };
  }

  nudge(direction) {
    const { min, max, step } = this.bounds();
    const current = Number(this.input.value) || min;
    const next = Math.min(max, Math.max(min, current + direction * step));
    if (next === current) return;
    this.input.value = String(next);
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  clamp() {
    const { min, max, step } = this.bounds();
    const raw = Number(this.input.value);
    let value = Number.isFinite(raw) ? raw : min;
    value = Math.round((value - min) / step) * step + min;
    this.input.value = String(Math.min(max, Math.max(min, value)));
    this.sync();
  }

  sync() {
    const { min, max } = this.bounds();
    const value = Number(this.input.value) || min;
    this.querySelectorAll('[data-qty-step]').forEach((button) => {
      const direction = Number(button.dataset.qtyStep) || 0;
      const atEnd = direction < 0 ? value <= min : value >= max;
      button.setAttribute('aria-disabled', String(atEnd));
    });
  }
}

/* ---------- Quick view gallery ---------- */

class MwQuickGallery extends HTMLElement {
  connectedCallback() {
    if (this.wired) return;
    this.wired = true;
    this.track = this.querySelector('[data-gallery-track]');
    if (!this.track) return;
    this.slides = [...this.track.children];
    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const slide = this.slides.find((item) => item.id === thumb.dataset.galleryThumb);
        if (!slide) return;
        // Scrolling the track directly keeps the dialog body where the shopper left it.
        this.track.scrollTo({
          left: slide.offsetLeft - this.track.offsetLeft,
          top: slide.offsetTop - this.track.offsetTop,
          behavior: reducedMotion.matches ? 'auto' : 'smooth',
        });
      });
    });

    // Arrows ([data-gallery-step]): one slide back or forward; the ends disable.
    this.arrows = [...this.querySelectorAll('[data-gallery-step]')];
    this.current = 0;
    this.arrows.forEach((arrow) => {
      arrow.addEventListener('click', () => {
        const next = Math.min(this.slides.length - 1, Math.max(0, this.current + Number(arrow.dataset.galleryStep)));
        const slide = this.slides[next];
        if (!slide) return;
        this.track.scrollTo({
          left: slide.offsetLeft - this.track.offsetLeft,
          top: slide.offsetTop - this.track.offsetTop,
          behavior: reducedMotion.matches ? 'auto' : 'smooth',
        });
      });
    });

    if (!this.thumbs.length && !this.arrows.length) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.current = this.slides.indexOf(entry.target);
          this.arrows.forEach((arrow) => {
            const step = Number(arrow.dataset.galleryStep);
            arrow.disabled = step < 0 ? this.current <= 0 : this.current >= this.slides.length - 1;
          });
          this.thumbs.forEach((thumb) => {
            const current = thumb.dataset.galleryThumb === entry.target.id;
            thumb.setAttribute('aria-current', String(current));
            if (current) this.reveal(thumb);
          });
        });
      },
      { root: this.track, threshold: 0.6 }
    );
    this.slides.forEach((slide) => this.observer.observe(slide));
  }

  // Keep the current thumbnail inside its own scroller (a column on desktop, a row on phones)
  // without moving the page, which scrollIntoView would do.
  reveal(thumb) {
    const box = thumb.parentElement;
    if (!box) return;
    const vertical = box.scrollHeight > box.clientHeight + 1;
    const pad = 48;
    if (vertical) {
      const top = thumb.offsetTop - pad;
      const bottom = thumb.offsetTop + thumb.offsetHeight + pad;
      if (top < box.scrollTop) box.scrollTo({ top, behavior: 'smooth' });
      else if (bottom > box.scrollTop + box.clientHeight) box.scrollTo({ top: bottom - box.clientHeight, behavior: 'smooth' });
    } else if (box.scrollWidth > box.clientWidth + 1) {
      const left = thumb.offsetLeft - pad;
      const right = thumb.offsetLeft + thumb.offsetWidth + pad;
      if (left < box.scrollLeft) box.scrollTo({ left, behavior: 'smooth' });
      else if (right > box.scrollLeft + box.clientWidth) box.scrollTo({ left: right - box.clientWidth, behavior: 'smooth' });
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

/* ---------- Show more ---------- */

let moreId = 0;

class MwMore extends HTMLElement {
  connectedCallback() {
    if (this.wired) return;
    this.wired = true;
    const text = this.querySelector('[data-more-text]');
    const toggle = this.querySelector('[data-more-toggle]');
    if (!text || !toggle) return;

    this.style.setProperty('--more-lines', String(Number(this.dataset.lines) || 3));
    this.classList.add('is-clamped');
    if (!text.id) text.id = `MwMore-${(moreId += 1)}`;
    toggle.setAttribute('aria-controls', text.id);
    toggle.setAttribute('aria-expanded', 'false');

    requestAnimationFrame(() => {
      const clipped = text.scrollHeight - text.clientHeight > 2;
      toggle.hidden = !clipped;
      if (!clipped) this.classList.remove('is-clamped');
    });

    toggle.addEventListener('click', () => {
      const clamped = this.classList.toggle('is-clamped');
      toggle.setAttribute('aria-expanded', String(!clamped));
      const show = this.querySelector('[data-more-show]');
      const hide = this.querySelector('[data-more-hide]');
      if (show) show.hidden = !clamped;
      if (hide) hide.hidden = clamped;
    });
  }
}

/* ---------- Add to cart ---------- */

const addedTimers = new WeakMap();

function announce(form, message) {
  const status = form.querySelector('[data-form-status]');
  if (!status || !message) return;
  status.textContent = '';
  // A short delay makes a repeated message read as a new announcement.
  setTimeout(() => {
    status.textContent = message;
  }, 100);
}

function setBusy(form, button, busy) {
  form.classList.toggle('is-busy', busy);
  if (!button) return;
  if (busy) {
    button.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-disabled', 'true');
  } else {
    button.removeAttribute('aria-busy');
    button.removeAttribute('aria-disabled');
  }
}

document.addEventListener('submit', async (event) => {
  const form = event.target.closest?.('form[data-product-form]');
  // Without card.js there is nothing to enhance with: let the browser post the form.
  if (!form || typeof window.Munkwell?.card?.add !== 'function') return;
  // Apps put their own fields in the product form: a subscription app's selling_plan, a bundle or
  // personalisation app's properties[…]. All of it goes with the line. A file upload cannot travel
  // as JSON, so a form carrying one is left to the browser's own post to /cart/add.
  const data = new FormData(form);
  if ([...data.values()].some((value) => value instanceof File && value.size > 0)) return;
  event.preventDefault();

  const button = form.querySelector('.pform__add');
  if (button?.getAttribute('aria-busy') === 'true') return;
  const id = Number(data.get('id'));
  const quantity = Number(data.get('quantity')) || 1;
  if (!id) return;
  // The offer ladder (assets/product-offer.js) may ask for two lines, one per colour.
  let offerItems = null;
  try {
    offerItems = form.dataset.offerItems ? JSON.parse(form.dataset.offerItems) : null;
  } catch {}

  const item = { id, quantity };
  const plan = data.get('selling_plan');
  if (plan) item.selling_plan = Number(plan);
  const properties = {};
  for (const [key, value] of data) {
    const match = /^properties\[(.+)\]$/.exec(key);
    if (match && typeof value === 'string' && value !== '') properties[match[1]] = value;
  }
  if (Object.keys(properties).length) item.properties = properties;

  const error = form.querySelector('[data-form-error]');
  const added = form.querySelector('[data-form-added]');
  if (error) {
    error.hidden = true;
    error.textContent = '';
  }
  setBusy(form, button, true);

  try {
    await window.Munkwell.card.add(offerItems || [item], form);
    if (form.dataset.cartPage === 'true') {
      window.location.assign(window.Munkwell?.routes?.cart || '/cart');
      return;
    }
    announce(form, form.dataset.success);
    if (added) added.hidden = false;
    if (button) {
      button.classList.add('is-added');
      clearTimeout(addedTimers.get(button));
      addedTimers.set(
        button,
        setTimeout(() => button.classList.remove('is-added'), ADDED_MS)
      );
    }
    // Once a cart drawer exists in the Overlay group, hand over to it the way Helix does.
    const drawer = document.querySelector('dialog.overlay[data-cart-drawer]');
    if (drawer && window.Munkwell?.overlay) window.Munkwell.overlay.open(drawer, null);
  } catch (failure) {
    const message = failure?.userMessage || form.dataset.error;
    if (error && message) {
      error.hidden = false;
      error.textContent = message;
    }
    announce(form, message);
  } finally {
    setBusy(form, button, false);
  }
});

/* ---------- Wiring ---------- */

if (!customElements.get('mw-variant-picker')) customElements.define('mw-variant-picker', MwVariantPicker);
if (!customElements.get('mw-quantity')) customElements.define('mw-quantity', MwQuantity);
if (!customElements.get('mw-quick-gallery')) customElements.define('mw-quick-gallery', MwQuickGallery);
if (!customElements.get('mw-more')) customElements.define('mw-more', MwMore);

window.Munkwell = window.Munkwell || {};
window.Munkwell.quick = { open };
